import React from 'react';
import { useNavigate } from 'react-router-dom';
import useModuleProgress from '../hooks/useModuleProgress';
import '../styles/pages/_module-pages.scss';
import '../styles/pages/_learn-assessments.scss';
import '../styles/pages/_quiz-completion.scss';

interface ModuleSection {
  title: string;
  content: string[];
  illustrationText?: string;
  additionalSection?: {
    title: string;
    content: string[];
  };
  additionalSection2?: {
    title: string;
    content: string[];
  };
}

const moduleContent: ModuleSection[] = [
  {
    title: 'Nutrition for Special Conditions',
    content: [
      '<strong>Goal:</strong> To understand how nutritional needs vary across different health conditions and life stages, and learn how to adapt your diet accordingly.'
    ],
    additionalSection: {
      title: 'Understanding Special Nutritional Needs',
      content: [
        '<strong>Core Concept:</strong> Different health conditions and life stages require specific nutritional approaches.',
        '<strong>Importance:</strong> Proper nutrition can help manage health conditions and support different life phases.',
        '<strong>Personalization:</strong> Learning to adapt general nutrition guidelines to specific needs.',
        '<br/>',
        '<strong>Key Message:</strong> "Nutrition should be personalized to your needs."'
      ]
    },
    additionalSection2: {
      title: 'Learning Objectives',
      content: [
        '<strong>1. Health Conditions:</strong> Understand nutrition for common health issues.',
        '<strong>2. Life Stages:</strong> Learn dietary needs across different ages.',
        '<strong>3. Special Diets:</strong> Explore various dietary restrictions and alternatives.',
        '<strong>4. Adaptation Skills:</strong> Master modifying recipes for special needs.'
      ]
    }
  },
  {
    title: 'Nutrition for Common Health Conditions',
    content: [
      'Dietary considerations for various health conditions:',
      `<div class="module-table">
        <div class="table-row header-row">
          <div class="table-header">Condition</div>
          <div class="table-header">Key Considerations</div>
          <div class="table-header">Recommended Foods</div>
        </div>
        <div class="table-row">
          <div class="table-cell pillar-cell">Diabetes</div>
          <div class="table-cell means-cell">Monitor carbohydrates, regular timing</div>
          <div class="table-cell matters-cell">High-fiber foods, lean proteins, complex carbs</div>
        </div>
        <div class="table-row">
          <div class="table-cell pillar-cell">Hypertension</div>
          <div class="table-cell means-cell">Reduce sodium, increase potassium</div>
          <div class="table-cell matters-cell">Leafy greens, bananas, fish, whole grains</div>
        </div>
        <div class="table-row">
          <div class="table-cell pillar-cell">Heart Disease</div>
          <div class="table-cell means-cell">Limit saturated fats, increase fiber</div>
          <div class="table-cell matters-cell">Oats, fatty fish, nuts, vegetables</div>
        </div>
      </div>`,
      '<br/>',
      '<strong>Important Note:</strong> Always consult healthcare providers for personalized advice.',
      '<br/>',
      '<strong>Activity: Condition-Specific Meal Planning</strong>',
      'Practice creating a day\'s meal plan for one health condition, considering:',
      '• Required nutrients',
      '• Foods to avoid',
      '• Timing of meals'
    ]
  },
  {
    title: 'Life Stage Nutrition',
    content: [
      'Nutritional needs across different life stages:',
      `<div class="module-table">
        <div class="table-row header-row">
          <div class="table-header">Life Stage</div>
          <div class="table-header">Special Needs</div>
          <div class="table-header">Focus Foods</div>
        </div>
        <div class="table-row">
          <div class="table-cell pillar-cell">Pregnancy</div>
          <div class="table-cell means-cell">Folate, iron, calcium</div>
          <div class="table-cell matters-cell">Leafy greens, fortified cereals, dairy</div>
        </div>
        <div class="table-row">
          <div class="table-cell pillar-cell">Growing Children</div>
          <div class="table-cell means-cell">Protein, calcium, vitamins</div>
          <div class="table-cell matters-cell">Milk, eggs, fruits, vegetables</div>
        </div>
        <div class="table-row">
          <div class="table-cell pillar-cell">Older Adults</div>
          <div class="table-cell means-cell">Protein, vitamin D, B12</div>
          <div class="table-cell matters-cell">Fish, fortified foods, lean meats</div>
        </div>
      </div>`,
      'Each life stage requires specific attention to certain nutrients.'
    ]
  },
  {
    title: 'Special Dietary Requirements',
    content: [
      'Understanding different dietary restrictions and alternatives:',
      `<div class="module-table">
        <div class="table-row header-row">
          <div class="table-header">Diet Type</div>
          <div class="table-header">Key Challenges</div>
          <div class="table-header">Alternative Solutions</div>
        </div>
        <div class="table-row">
          <div class="table-cell pillar-cell">Vegetarian</div>
          <div class="table-cell means-cell">Protein, B12, iron</div>
          <div class="table-cell matters-cell">Legumes, fortified foods, eggs/dairy</div>
        </div>
        <div class="table-row">
          <div class="table-cell pillar-cell">Gluten-Free</div>
          <div class="table-cell means-cell">Finding alternatives</div>
          <div class="table-cell matters-cell">Rice, corn, quinoa products</div>
        </div>
        <div class="table-row">
          <div class="table-cell pillar-cell">Lactose-Free</div>
          <div class="table-cell means-cell">Calcium intake</div>
          <div class="table-cell matters-cell">Plant-based milk, leafy greens</div>
        </div>
      </div>`,
      '<br/>',
      '<strong>Recipe Modification Tips:</strong>',
      '• Learn equivalent substitutions',
      '• Maintain nutritional balance',
      '• Consider texture and taste'
    ]
  },
  {
    title: 'Personal Adaptation Plan',
    content: [
      'Create your personalized nutrition strategy:',
      '• <strong>Step 1:</strong> Identify your specific nutritional needs',
      '• <strong>Step 2:</strong> List foods that meet these needs',
      '• <strong>Step 3:</strong> Plan meals incorporating these foods',
      '• <strong>Step 4:</strong> Track and adjust as needed',
      '<br/>',
      '<strong>Weekly Challenge:</strong>',
      'Create a 3-day meal plan that addresses your specific nutritional needs.',
      '<br/>',
      '<strong>Remember:</strong> Good nutrition is about finding what works for YOUR body and lifestyle.'
    ],
    illustrationText: 'Personalized Nutrition'
  },
  {
    title: 'Ready to Test Your Knowledge?',
    content: [
      "You've completed the module content! Now, let's see how well you understand nutrition for special conditions and life stages."
    ],
    additionalSection: {
      title: 'Next Steps',
      content: [
        'Click "Take the Quiz" below to test your understanding of nutrition for different health conditions and life stages.',
        'The quiz will help you review key concepts about adapting nutrition for special needs and personalized dietary strategies.'
      ]
    }
  }
];

const NutritionalModule6Page: React.FC = () => {
  const navigate = useNavigate();
  const { currentStep, setCurrentStep, isLoading, saveProgress, isCompleted } = useModuleProgress('nutritionalM6');
  const totalSteps = moduleContent.length;

  // Ensure currentStep is within bounds
  const safeCurrentStep = Math.max(0, Math.min(currentStep, totalSteps - 1));
  const currentModule = moduleContent[safeCurrentStep];
  const progress = ((safeCurrentStep + 1) / totalSteps) * 100;

  const handleNext = async () => {
    if (currentStep < totalSteps - 1) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      await saveProgress(nextStep, false);
    } else {
      await saveProgress(currentStep, true);
      navigate('/panel/learn/nutritional-module-6/quiz');
    }
  };

  const handleBack = async () => {
    if (currentStep === 0) {
      navigate('/panel/learn/nutritional-resources');
      return;
    }
    const prevStep = currentStep - 1;
    setCurrentStep(prevStep);
    await saveProgress(prevStep, isCompleted);
  };

  const showCompletionMessage = currentStep === totalSteps - 1;

  if (isLoading) {
    return (
      <div className="module-form" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>Loading your progress...</p>
      </div>
    );
  }

  // Error boundary for undefined module
  if (!currentModule) {
    return (
      <div className="module-form" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>Error loading module content. Please try refreshing the page.</p>
      </div>
    );
  }

  return (
    <div className="module-form">
      <div className="module-container">
        <div 
          className="module-left nutritional-panel"
        >
          <div className="module-header">
            <h1>Module 6</h1>
            <p className="module-subtitle">Nutrition for Long-Term Health</p>
            <hr className="module-divider" />
            <p className="module-tool-info">
              Understand how good nutrition supports long-term health and learn strategies to maintain healthy eating habits for life.
            </p>
          </div>
        </div>

        <div className="module-right">
          <div className="module-content"> 
            <div className="module-progress">
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${progress}%` }}></div>
              </div>
              <span className="progress-text">
                Page {currentStep + 1} of {totalSteps}
              </span>
            </div>

            <div className="module-section">
              <h2 className="module-title"> 
                {currentModule.title}
              </h2>
              <div className="content-section">
                {currentModule.content.map((paragraph, index) => (
                  <p key={index} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                ))}
              </div>
            </div>

            {currentModule.additionalSection && (
              <div className="module-section">
                <h2 className="module-title">{currentModule.additionalSection.title}</h2>
                <div className="content-section">
                  {currentModule.additionalSection.content.map((paragraph: string, index: number) => (
                    <p key={`additional-${index}`} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                  ))}
                </div>
              </div>
            )}
            {currentModule.additionalSection2 && (
              <div className="module-section">
                <h2 className="module-title">{currentModule.additionalSection2.title}</h2>
                <div className="content-section">
                  {currentModule.additionalSection2.content.map((paragraph: string, index: number) => (
                    <p key={`additional2-${index}`} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="navigation-buttons">
            <button
              className="btn-secondary"
              onClick={handleBack}
              disabled={false} 
            >
              {currentStep === 0 ? 'Exit Module' : 'Previous'}
            </button>
            
            {showCompletionMessage ? (
              <button
                className="btn-primary"
                onClick={() => navigate('/panel/learn/nutritional-module-6/quiz')}
              >
                Take the Quiz
              </button>
            ) : (
              <button
                className="btn-primary"
                onClick={handleNext}
              >
                Next
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NutritionalModule6Page;

